import classNames from 'classnames'
import { h, JSX } from 'preact'
import { useRef, useState } from 'preact/hooks'

import { ArrowRightIcon } from '../icon/icon'

export interface ButtonProps {
    text: string
    element?: 'button' | 'a'
    url?: string
    type?: string
    onClick?: () => unknown
    hasIcon?: boolean
    modifier?: string
    class?: string
    isDisabled?: boolean
    attributes?: JSX.HTMLAttributes<HTMLButtonElement> & JSX.HTMLAttributes<HTMLAnchorElement>
}

interface Coords {
    clientX: number
    clientY: number
}

type MouseOrTouchEvent = JSX.TargetedEvent<Element, MouseEvent | TouchEvent>

const isMouseEvent = (
    event: MouseOrTouchEvent,
): event is JSX.TargetedEvent<Element, MouseEvent> => {
    return 'pageX' in event
}

const isTouchEvent = (
    event: MouseOrTouchEvent,
): event is JSX.TargetedEvent<Element, TouchEvent> => {
    return 'touches' in event
}

const getCoords = (event: MouseOrTouchEvent): Coords => {
    if (isMouseEvent(event)) {
        return event
    } else if (isTouchEvent(event)) {
        return event.touches[0]
    } else {
        return { clientX: 0, clientY: 0 }
    }
}

export const Button = (props: ButtonProps): JSX.Element => {
    const {
        text,
        element: Element = 'button',
        url = '#',
        type = 'button',
        hasIcon,
        modifier,
        isDisabled,
        attributes,
    } = props

    const buttonRef = useRef<HTMLButtonElement & HTMLAnchorElement>(null)
    const [isAnimating, setIsAnimating] = useState(false)
    const [inkStyle, setInkStyle] = useState<JSX.CSSProperties>({})

    const onClick = async (event: MouseOrTouchEvent) => {
        const button = buttonRef.current

        if (button) {
            setIsAnimating(false)

            // Let the isAnimating change take effect
            await new Promise((resolve) => setTimeout(resolve, 0))

            const { clientX, clientY } = getCoords(event)
            const buttonRect = button.getBoundingClientRect()
            const size = Math.max(buttonRect.width, buttonRect.height)

            setInkStyle({
                width: size,
                height: size,
                left: clientX - buttonRect.left - size / 2,
                top: clientY - buttonRect.top - size / 2,
            })

            setIsAnimating(true)
        }

        props.onClick?.()
    }

    return (
        <Element
            ref={buttonRef}
            href={Element === 'a' ? url : undefined}
            type={Element === 'button' ? type : undefined}
            class={classNames('btn', modifier, props.class, { 'btn--with-icon': hasIcon })}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={onClick}
            disabled={isDisabled}
            {...attributes}
        >
            {text}
            <span
                class={classNames('btn__ink', { 'is-animating': isAnimating })}
                style={inkStyle}
            />
            {hasIcon && <ArrowRightIcon class="btn__icon" />}
        </Element>
    )
}
